import React from 'react'
import {type ListRenderItemInfo, View} from 'react-native'
import {type AppBskyFeedDefs as FeedDefs} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {cleanError} from '#/lib/strings/errors'
import {useTapAllPostsQuery} from '#/state/queries/tap-all-posts'
import {Post} from '#/view/com/post/Post'
import {List, type ListMethods} from '#/view/com/util/List'
import {ListFooter, ListMaybePlaceholder} from '#/components/Lists'
import {useSetMinimalShellMode} from '#/state/shell'
import {MainScrollProvider} from '#/view/com/util/MainScrollProvider'
import {useHeaderOffset} from '#/components/hooks/useHeaderOffset'
import {LoadLatestBtn} from '#/view/com/util/load-latest/LoadLatestBtn'
import {s} from '#/lib/styles'
import {ComposeIcon2} from '#/lib/icons'
import {FAB} from '#/view/com/util/fab/FAB'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {useSession} from '#/state/session'
import {logEvent} from '#/lib/statsig/statsig'

type PostView = FeedDefs.PostView

const renderItem = ({item}: ListRenderItemInfo<PostView>) => {
  // Debug: Log what Post component receives
  if (item.uri?.endsWith('vq4ewea22e')) {
    console.log('[MediaFeedPage] Rendering specific post:', {
      uri: item.uri?.slice(-15),
      likeCount: item.likeCount,
      repostCount: item.repostCount,
      replyCount: item.replyCount,
      hasViewer: !!item.viewer,
      viewerLike: item.viewer?.like,
    })
  }
  return <Post post={item} />
}

const keyExtractor = (item: PostView, index: number) => {
  return `${item.uri}-${index}`
}

const initialNumToRender = 14

export function MediaFeedPage({
  testID,
  mediaType,
  isPageFocused,
  isPageAdjacent,
}: {
  testID?: string
  mediaType: 'images' | 'videos'
  isPageFocused: boolean
  isPageAdjacent: boolean
}) {
  const {_} = useLingui()
  const {hasSession} = useSession()
  const [isPTR, setIsPTR] = React.useState(false)
  const setMinimalShellMode = useSetMinimalShellMode()
  const headerOffset = useHeaderOffset()
  const {openComposer} = useOpenComposer()
  const scrollElRef = React.useRef<ListMethods>(null)
  const [hasNew, setHasNew] = React.useState(false)

  // Fetch all posts from all tags, then filter by media type
  const {
    data,
    isFetched,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useTapAllPostsQuery({
    enabled: isPageFocused || isPageAdjacent,
  })

  const basePosts = React.useMemo(() => {
    const allPosts = data?.pages.flatMap(page => page.posts) || []

    console.log('[MediaFeedPage] Processing posts:', {
      mediaType,
      totalPosts: allPosts.length,
      hasImages: allPosts.filter(p => p.hasImages).length,
      hasVideo: allPosts.filter(p => p.hasVideo).length,
      firstPostSample: allPosts[0] ? {
        uri: allPosts[0].uri?.slice(-15),
        hasImages: allPosts[0].hasImages,
        hasVideo: allPosts[0].hasVideo,
        hasAuthor: !!allPosts[0].author,
      } : null,
    })

    // Filter by media type using TAP API flags
    if (mediaType === 'images') {
      return allPosts.filter(post => post.hasImages)
    } else if (mediaType === 'videos') {
      return allPosts.filter(post => post.hasVideo)
    }

    return allPosts
  }, [data, mediaType])

  const posts = basePosts

  const onRefresh = React.useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
    setHasNew(false)
    logEvent('feed:refresh', {
      feedType: 'media',
      feedUrl: `media|${mediaType}`,
      reason: 'pull-to-refresh',
    })
  }, [refetch, mediaType])

  const onEndReached = React.useCallback(() => {
    if (isFetchingNextPage || !hasNextPage || error) return
    fetchNextPage()
  }, [isFetchingNextPage, hasNextPage, error, fetchNextPage])

  const scrollToTop = React.useCallback(() => {
    scrollElRef.current?.scrollToOffset({
      animated: true,
      offset: -headerOffset,
    })
    setMinimalShellMode(false)
  }, [headerOffset, setMinimalShellMode])

  const onPressLoadLatest = React.useCallback(() => {
    scrollToTop()
    refetch()
    setHasNew(false)
    logEvent('feed:refresh', {
      feedType: 'media',
      feedUrl: `media|${mediaType}`,
      reason: 'load-latest',
    })
  }, [scrollToTop, refetch, mediaType])

  const onPressCompose = React.useCallback(() => {
    openComposer({})
  }, [openComposer])

  const emptyMessage =
    mediaType === 'images'
      ? _(msg`No images found.`)
      : _(msg`No videos found.`)

  // Debug: Log render state
  console.log('[MediaFeedPage] Render state:', {
    mediaType,
    postsCount: posts.length,
    isLoading,
    isFetched,
    isError,
    error: error?.message,
    hasData: !!data,
    pagesCount: data?.pages?.length,
  })

  return (
    <View testID={testID} style={{flex: 1}}>
      <MainScrollProvider>
        {posts.length < 1 ? (
          <ListMaybePlaceholder
            isLoading={isLoading || !isFetched}
            isError={isError}
            onRetry={refetch}
            emptyType="results"
            emptyMessage={emptyMessage}
            errorMessage={cleanError(error)}
          />
        ) : (
          <List
            ref={scrollElRef}
            data={posts}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            refreshing={isPTR}
            onRefresh={onRefresh}
            onEndReached={onEndReached}
            onEndReachedThreshold={4}
            // @ts-ignore web only -prf
            desktopFixedHeight
            ListFooterComponent={
              <ListFooter
                isFetchingNextPage={isFetchingNextPage}
                error={cleanError(error)}
                onRetry={fetchNextPage}
                hasNextPage={hasNextPage}
              />
            }
            initialNumToRender={initialNumToRender}
            windowSize={11}
          />
        )}
      </MainScrollProvider>

      {hasNew && (
        <LoadLatestBtn
          onPress={onPressLoadLatest}
          label={_(msg`Load new posts`)}
          showIndicator={hasNew}
        />
      )}

      {hasSession && (
        <FAB
          testID="composeFAB"
          onPress={onPressCompose}
          icon={<ComposeIcon2 strokeWidth={1.5} size={29} style={s.white} />}
          accessibilityRole="button"
          accessibilityLabel={_(msg({message: `New post`, context: 'action'}))}
          accessibilityHint=""
        />
      )}
    </View>
  )
}
